import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { IThemeConfig } from '../interfaces/theme-config';

import { THEME_API } from './tokens';

/**
 * The purpose of this service is to fetch brand theme and apply it.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  /**
   * Initializes the ThemeService
   * @param http Angular's HttpClient
   * @param document Angular's DOM Document
   */
  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    @Inject(THEME_API) private themeAPI: string,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Requests theme from api endpoint
   */
  public fetch(): Observable<IThemeConfig> {
    return this.http.get(this.themeAPI).pipe(
      tap((theme: IThemeConfig) => {
        this.renderRootStyles(theme);
      }),
      catchError(() => []),
    );
  }

  /**
   * Iterates over theme config and applies it to document
   * @param theme Theme config to apply
   */
  private renderRootStyles(theme: IThemeConfig) {
    (theme.fontStylesheets ?? []).forEach((url) => this.downloadFontSheet(url));

    if (theme.light) {
      Object.keys(theme.light).forEach((key: string) => {
        // tslint:disable-next-line:no-non-null-assertion
        this.document.documentElement.style.setProperty(key, theme.light![key]);
      });
    }

    if (theme.dark) {
      this.renderDarkModeStyles(theme.dark);
    }
  }

  private renderDarkModeStyles(variables: {
    [key: string]: string | string[];
  }) {
    const style = this.renderer.createElement('style');

    const styleContent = Object.keys(variables)
      .map((key) => `${key}: ${variables[key]}`)
      .join(';');

    style.innerHTML = `html body.dark-mode { ${styleContent} }`;

    this.renderer.appendChild(this.document.head, style);
  }

  private downloadFontSheet(url: string) {
    const link = this.renderer.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.type = 'text/css';
    link.href = url;
    this.renderer.appendChild(this.document.head, link);
  }
}
